/**
 * `lib/motionTokens.ts` — the ONE motion vocabulary for the whole SPA
 * (#2172; first written for the member phone app, #1665 design §6.1).
 *
 * Decision D4 (founder, 11 Oct 2026) is CALM: iOS timings and curves,
 * things ease OUT (fast start, soft landing), and nothing overshoots
 * except a sheet rising and the phone tab icon. Every number here has a
 * CSS twin on `:root` in `styles/tokens.css` (`--dur-*`, `--ease-*`,
 * `--stagger-col`); `tests/motionTokens.test.ts` pins the two together, so
 * change them in the same commit. The member app re-exports these from
 * `memberapp/motion.ts`.
 *
 * Reduced motion: CSS motion collapses through the one global switch in
 * `styles/tailwind.css`. JS-driven motion asks `jsMotionEnabled()` and,
 * when it is false, renders the end state at once (or an opacity-only
 * fade of at most `DUR_FAST_MS`). Per-frame work animates `transform` /
 * `opacity` only.
 */

/** A press answers within one frame and springs back. */
export const DUR_PRESS_MS = 80;
/** Micro: a fade, a tooltip, a press release. Also the reduced-motion ceiling. */
export const DUR_FAST_MS = 120;
/** Element: a control's colour, a pill gliding, a menu growing. */
export const DUR_MED_MS = 200;
/** Screen: a page arriving, a sheet rising, a list cascading. */
export const DUR_SLOW_MS = 320;
/** The roster "land": a filled cell's contents arriving on the grid. */
export const DUR_LAND_MS = 420;
/** The station view (#2676): a character walks to its next spot at a session edge. */
export const DUR_WALK_MS = 1200;
/** The grid reveal's per-column stagger (Monday to Sunday). */
export const STAGGER_COL_MS = 45;

/** Ease-out (fast start, soft landing): the default for anything arriving. */
export const EASE_OUT_SOFT = "cubic-bezier(0.22, 1, 0.36, 1)";
/** Ease-in-out: anything leaving, or moving between two resting places. */
export const EASE_IN_OUT_MOVE = "cubic-bezier(0.65, 0, 0.35, 1)";
/** The one curve that overshoots. D4: sheets and the phone tab icon ONLY. */
export const EASE_SPRING_SOFT = "cubic-bezier(0.34, 1.56, 0.64, 1)";

/** A cubic bézier as `[x1, y1, x2, y2]` — `motion`'s `ease` option. */
export type BezierPoints = [number, number, number, number];
/** The same curves as `EASE_OUT_SOFT` / `EASE_IN_OUT_MOVE`, for `motion`. */
export const EASE_OUT_SOFT_POINTS: BezierPoints = [0.22, 1, 0.36, 1];
export const EASE_IN_OUT_MOVE_POINTS: BezierPoints = [0.65, 0, 0.35, 1];

/** `motion` takes seconds; the tokens are milliseconds. */
export function seconds(ms: number): number {
  return ms / 1000;
}

export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** JS-side check mirroring the CSS `motion-reduce:` variant. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    return false;
  }
}

let testEnvOverride: boolean | null = null;

/**
 * Tests only: decide whether the environment counts as a test runner
 * (`null` restores detection). The motion tests flip it to `false` to
 * exercise the real paths; everything else leaves it alone, so every
 * other test renders end states.
 */
export function setMotionTestEnvOverride(value: boolean | null): void {
  testEnvOverride = value;
}

function underTest(): boolean {
  if (testEnvOverride !== null) return testEnvOverride;
  if (import.meta.env?.MODE === "test") return true;
  return typeof navigator !== "undefined" && /happy-?dom|jsdom/i.test(navigator.userAgent);
}

/**
 * Whether JS-driven motion (a page arriving, a count, a cell flying) may
 * run here at all. False on the server, without `requestAnimationFrame`,
 * under a test runner (so a test reads the end state) and under
 * `prefers-reduced-motion`.
 */
export function jsMotionEnabled(): boolean {
  return jsMotionMode() === "full";
}

/**
 * `jsMotionEnabled` in three states, for motion that still owes the reader
 * something when they asked for less of it (the phone app's screens and
 * sheets cross-fade instead of sliding, #2172 M0):
 *   - `"full"` — move as designed;
 *   - `"reduced"` — `prefers-reduced-motion`: opacity only, at most
 *     `DUR_FAST_MS`;
 *   - `"off"` — the server, no `requestAnimationFrame`, or a test runner:
 *     end states at once.
 */
export type JsMotionMode = "full" | "reduced" | "off";

export function jsMotionMode(): JsMotionMode {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    return "off";
  }
  if (underTest()) return "off";
  return prefersReducedMotion() ? "reduced" : "full";
}
